import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Small key/value settings anyone may read. Today it carries one switch:
 * `checkout_legal_review_passed` — while it is "false" the membership checkout
 * button stays disabled and a review banner shows on the legal pages. Flipping
 * the value needs no deploy.
 */

export const CHECKOUT_GATE_KEY = "checkout_legal_review_passed";

/** True only for an explicit "true". Anything else keeps checkout closed. */
export function gateOpen(value: string | null | undefined): boolean {
  return (value ?? "").trim().toLowerCase() === "true";
}

export type CheckoutGate = {
  /** Whether the charge action is allowed to run at all. */
  open: boolean;
};

export const getCheckoutGate = createServerFn({ method: "GET" }).handler(
  async (): Promise<CheckoutGate> => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const supabasePublic = createClient(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false },
      global: {
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    const { data, error } = await supabasePublic
      .from("site_config")
      .select("value")
      .eq("key", CHECKOUT_GATE_KEY)
      .maybeSingle();

    // Fail closed: if we can't read the switch, checkout stays disabled.
    if (error) {
      console.error("checkout gate lookup failed", error);
      return { open: false };
    }

    return { open: gateOpen((data as { value?: string } | null)?.value) };
  },
);

/** Admin: flip the review gate without a deploy. */
export const setCheckoutGate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ open: z.boolean() }).parse(data))
  .handler(async ({ context, data }) => {
    // RLS lets only tenant admins and super admins write this table.
    const { error } = await context.supabase
      .from("site_config")
      .upsert({ key: CHECKOUT_GATE_KEY, value: data.open ? "true" : "false" } as never, {
        onConflict: "key",
      });

    if (error) {
      console.error("checkout gate update failed", error);
      throw new Error("Could not change that setting.");
    }

    return { ok: true, open: data.open };
  });

/**
 * Whether articles delivered by the external writing service go live straight
 * away. Off means every delivery waits in the blog manager as a draft.
 */
export const BLOG_AUTOPUBLISH_KEY = "blog_autopublish";

export const getBlogAutopublish = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ on: boolean }> => {
    const { data } = await context.supabase
      .from("site_config")
      .select("value")
      .eq("key", BLOG_AUTOPUBLISH_KEY)
      .maybeSingle();
    return { on: gateOpen((data as { value?: string } | null)?.value) };
  });

export const setBlogAutopublish = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ on: z.boolean() }).parse(data))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("site_config")
      .upsert({ key: BLOG_AUTOPUBLISH_KEY, value: data.on ? "true" : "false" } as never, {
        onConflict: "key",
      });
    if (error) {
      console.error("blog autopublish update failed", error);
      throw new Error("Could not change that setting.");
    }
    return { ok: true, on: data.on };
  });

/* ------------------------------------------------------------------ *
 * RSVP outcome wording, editable without a deploy
 * ------------------------------------------------------------------ */

export const CONFIRM_COPY_KEYS = [
  "copy_waitlist_confirm_title",
  "copy_waitlist_confirm_body",
  "copy_rsvp_confirm_title",
  "copy_rsvp_confirm_body",
  "copy_rsvp_returning_body",
  "copy_waitlist_email_prompt",
  "copy_rsvp_email_prompt",
] as const;

export type ConfirmCopyKey = (typeof CONFIRM_COPY_KEYS)[number];
export type ConfirmCopy = Record<ConfirmCopyKey, string>;

/** Wording used if the settings row is missing. */
export const CONFIRM_COPY_FALLBACK: ConfirmCopy = {
  copy_waitlist_confirm_title: "You're on the list",
  copy_waitlist_confirm_body: "You're on the waiting list. We'll email you when a place opens.",
  copy_rsvp_confirm_title: "You're in",
  copy_rsvp_confirm_body:
    "You're attending. Watch your email, including spam, for venue and arrival details.",
  copy_rsvp_returning_body: "Good to see you again — your standing is intact and you're in.",
  copy_waitlist_email_prompt: "Add your email so we can tell you when a place opens.",
  copy_rsvp_email_prompt: "Add your email so we can send venue and arrival details.",
};

/** Public read of the editable RSVP outcome wording. */
export const getConfirmCopy = createServerFn({ method: "GET" }).handler(
  async (): Promise<ConfirmCopy> => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const supabasePublic = createClient(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false },
      global: {
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    const { data, error } = await supabasePublic
      .from("site_config")
      .select("key, value")
      .in("key", CONFIRM_COPY_KEYS as unknown as string[]);

    if (error) {
      console.error("RSVP copy lookup failed", error);
      return CONFIRM_COPY_FALLBACK;
    }

    const rows = (data ?? []) as { key: string; value: string | null }[];
    const copy = { ...CONFIRM_COPY_FALLBACK };
    for (const row of rows) {
      if ((CONFIRM_COPY_KEYS as readonly string[]).includes(row.key) && row.value?.trim()) {
        copy[row.key as ConfirmCopyKey] = row.value.trim();
      }
    }
    return copy;
  },
);

/** Crew edit of any one RSVP outcome wording entry. */
export const setConfirmCopy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ key: z.enum(CONFIRM_COPY_KEYS), value: z.string().trim().min(1).max(600) })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("site_config")
      .upsert({ key: data.key, value: data.value } as never, { onConflict: "key" });
    if (error) {
      console.error("RSVP copy update failed", error);
      throw new Error("Could not save that wording.");
    }
    return { ok: true as const };
  });

/* Waivers switch: off until SquadFlight has a waiver. Missing row = off. */
export const WAIVERS_KEY = "waivers_enabled";

export const getWaiversEnabled = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ on: boolean }> => {
    const { data } = await context.supabase
      .from("site_config")
      .select("value")
      .eq("key", WAIVERS_KEY)
      .maybeSingle();
    return { on: gateOpen((data as { value?: string } | null)?.value) };
  });

export const setWaiversEnabled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ on: z.boolean() }).parse(data))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("site_config")
      .upsert({ key: WAIVERS_KEY, value: data.on ? "true" : "false" } as never, {
        onConflict: "key",
      });
    if (error) {
      console.error("waivers switch update failed", error);
      throw new Error("Could not change that setting.");
    }
    return { ok: true, on: data.on };
  });
